import PageHero from "@/components/PageHero";
import { IMG_SERVICES_RES, IMG_SERVICES_COMMUNITY } from "@/lib/constants";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";

const solutions = [
  {
    eyebrow: "Explore Your Options",
    title: "Honest options. Honest answers.",
    body: "Every situation is different, and every homeowner deserves a clear-eyed look at what's actually possible. We sit down with you, walk through your circumstances, and lay out the real paths forward — including referrals to assistance programs when they fit. In many cases, the best path is a creative purchase structured to protect your equity and your timeline. Either way, you'll leave the conversation with clarity — not pressure.",
    points: [
      "A free, no-obligation conversation about your situation",
      "Referrals to community assistance programs when they're a fit",
      "Creative purchase structures designed to protect your equity",
    ],
    image: IMG_SERVICES_RES,
    align: "left",
  },
  {
    eyebrow: "Dignified Exit",
    title: "A respectful path forward.",
    body: "When staying isn't possible, we offer transparent, fair, and dignified exit strategies designed to protect a homeowner's equity, time, and peace of mind during difficult transitions.",
    points: [
      "Fair, transparent valuation",
      "Flexible timelines built around the family",
      "No high-pressure tactics — ever",
    ],
    image: IMG_SERVICES_COMMUNITY,
    align: "right",
  },
  {
    eyebrow: "Community Investment",
    title: "Strengthening neighborhoods.",
    body: "We invest in properties and projects that improve housing inventory and uplift the neighborhoods we love. Every investment is measured by community impact, not just returns.",
    points: [
      "Property restoration & responsible redevelopment",
      "Investments that improve local housing stock",
      "Partnerships with community-aligned stakeholders",
    ],
    image: IMG_SERVICES_RES,
    align: "left",
  },
  {
    eyebrow: "Advocacy & Education",
    title: "Knowledge is stability.",
    body: "We provide educational resources, host community conversations, and partner with local organizations to help homeowners understand their rights, options, and long-term opportunities.",
    points: [
      "Homeowner education resources",
      "Community workshops and listening sessions",
      "Partnerships with local nonprofits and agencies",
    ],
    image: IMG_SERVICES_COMMUNITY,
    align: "right",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHero
        testIdPrefix="services-hero"
        eyebrow="Solutions"
        title="Real solutions for real challenges."
        subtitle="Every situation is different. We bring practical, people-first solutions to the housing challenges Hawaii families face."
      />

      {/* Solutions Alternating */}
      <section className="bg-brand-ivory" data-testid="services-list">
        {solutions.map((s, i) => (
          <div
            key={s.title}
            data-testid={`solution-${i}`}
            className={`py-20 md:py-28 ${i % 2 === 1 ? "bg-white border-y border-brand-forest/10" : ""}`}
          >
            <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
              <div className={`grid lg:grid-cols-12 gap-10 lg:gap-16 items-center ${s.align === "right" ? "lg:[&>div:first-child]:order-2" : ""}`}>
                <div className="lg:col-span-6">
                  <div className="aspect-[4/3] overflow-hidden rounded-sm bg-brand-forest/5">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                      data-testid={`solution-${i}-image`}
                    />
                  </div>
                </div>
                <div className="lg:col-span-6">
                  <p className="eyebrow text-brand-gold">{s.eyebrow}</p>
                  <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight">
                    {s.title}
                  </h2>
                  <p className="mt-6 text-brand-ink/75 text-base md:text-lg leading-relaxed font-light">
                    {s.body}
                  </p>
                  <ul className="mt-8 space-y-3">
                    {s.points.map((pt, j) => (
                      <li key={pt} data-testid={`solution-${i}-point-${j}`} className="flex items-start gap-3 text-brand-ink/85">
                        <Check size={18} className="text-brand-gold mt-1 shrink-0" />
                        <span className="text-sm md:text-base">{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="bg-brand-forest text-white py-24 md:py-28" data-testid="services-cta">
        <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-12 text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl leading-tight">
            Not sure where you fit? <br />
            <span className="text-brand-gold italic">Start with a conversation.</span>
          </h2>
          <p className="mt-6 text-white/80 max-w-2xl mx-auto text-lg font-light">
            We listen first. Reach out and we'll help you understand your options — at no cost and no pressure.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="mt-10 inline-flex items-center gap-2 px-8 py-4 bg-brand-gold text-brand-ink text-[12px] uppercase tracking-[0.24em] font-semibold rounded-sm hover:bg-white transition-all duration-300"
          >
            Contact Us <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
